import { computed, defineComponent, ref } from "@vue/composition-api";
import { useI18n } from "vue-i18n-composable";
import { required, minLength, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { useAddress } from "@Shared/@Composables/generic/useAddress";
import Err from "@Broker/@Routes/error";
import { searchParticipants } from "@Broker/@Modules/clients/applications/application/api/application-api";
import { useVuelidate } from "@Shared/@Composables/validations/useVuelidate";
import GenericModal from "@Shared/components/modals/generic-modal.vue";
import SimpleButton from "@Shared/components/buttons/simple-button.vue";
import { RadioSection } from "@Shared/components/sections";
import MaterialInput from "@Shared/components/inputs/material-input.vue";
import ProfileAvatar from "@Shared/components/general/profile-avatar.vue";
import TablePagination from "@Shared/components/general/table-pagination.vue";
import { isEmpty } from "lodash";
import NotificationPopover from "@Shared/components/general/notification-popover.vue";
export default defineComponent({
  name: "AddParticipantModal",
  components: {
    GenericModal,
    SimpleButton,
    RadioSection,
    MaterialInput,
    ProfileAvatar,
    TablePagination,
    NotificationPopover
  },
  mixins: [validationMixin],
  validations: {
    firstName: {
      required,
      minLength: minLength(2)
    },
    lastName: {
      required,
      minLength: minLength(2)
    },
    ownFirmOnly: {
      required
    },
    selectedParticipant: {
      required: requiredIf(vm => vm.showResults)
    }
  },
  setup(props, {
    emit
  }) {
    const {
      t,
      tc
    } = useI18n();
    const {
      v$
    } = useVuelidate();
    const addParticipantModal = ref();
    const participantsTable = ref();
    const loading = ref(false);
    const loadingSucceeded = ref(true);
    const firstName = ref(null);
    const lastName = ref(null);
    const ownFirmOnly = ref(true);
    const showResults = ref(false);
    const selectedParticipant = ref({});
    const limit = 10;
    const offset = ref(0);
    const results = ref();
    const firmOptions = computed(() => [{
      label: t("direct-to-lender.commissions.commissionDetails.ownFirm"),
      value: true
    }, {
      label: t("direct-to-lender.commissions.commissionDetails.allFirms"),
      value: false
    }]);
    const options = computed(() => ({
      filterable: false,
      sortable: [],
      columns: ["fullName", "brokerage", "address", "selected"],
      headings: {
        fullName: t("tables.columns.fullName"),
        brokerage: t("global.doorr.brokerage"),
        address: tc("global.words.address", 1),
        selected: t("direct-to-lender.commissions.commissionDetails.selectOne")
      },
      columnsClasses: {
        fullName: "w-30",
        brokerage: "w-20",
        address: "w-30",
        selected: "w-20"
      },
      skin: "table table-hover",
      perPage: limit,
      texts: {
        noResults: t("tables.messages.emptyAgents")
      }
    }));
    const componentAttributes = computed(() => showResults.value ? {
      modalSize: "lg",
      modalTitle: t("direct-to-lender.commissions.commissionDetails.searchResults"),
      nextButtonText: t("direct-to-lender.commissions.commissionDetails.addParticipant")
    } : {
      modalSize: "xs",
      modalTitle: t("direct-to-lender.commissions.commissionDetails.agentSearch"),
      nextButtonText: t("global.words.search")
    });
    const fullAddress = address => useAddress(address).fullAddress.value;
    const openModal = () => {
      var _addParticipantModal$;
      (_addParticipantModal$ = addParticipantModal.value) == null ? void 0 : _addParticipantModal$.show();
    };
    const closeModal = () => {
      var _addParticipantModal$2;
      (_addParticipantModal$2 = addParticipantModal.value) == null ? void 0 : _addParticipantModal$2.hide();
    };
    const resetData = () => {
      var _v$$value;
      ownFirmOnly.value = true;
      firstName.value = null;
      lastName.value = null;
      selectedParticipant.value = {};
      results.value = [];
      loading.value = false;
      loadingSucceeded.value = true;
      showResults.value = false;
      (_v$$value = v$.value) == null ? void 0 : _v$$value.$reset();
    };
    const handleSelectAgent = participant => {
      selectedParticipant.value = participant;
    };
    const handleSubmit = async () => {
      if (showResults.value) {
        emit("selected-participant", {
          selectedParticipant: selectedParticipant.value
        });
        closeModal();
        return;
      }
      try {
        loading.value = true;
        const {
          agents
        } = await searchParticipants(ownFirmOnly.value, firstName.value, lastName.value);
        results.value = agents;
        loadingSucceeded.value = true;
        showResults.value = true;
      } catch (e) {
        Err.handle(e);
        loadingSucceeded.value = false;
      }
      loading.value = false;
    };
    const buttonWarning = computed(() => {
      return !isEmpty(selectedParticipant.value) && selectedParticipant.value.expertProOnlyUser;
    });
    const handlePageChange = ({
      page,
      offset: newOffset
    }) => {
      var _participantsTable$va;
      (_participantsTable$va = participantsTable.value) == null ? void 0 : _participantsTable$va.setPage(page);
      offset.value = newOffset;
    };
    return {
      addParticipantModal,
      participantsTable,
      firstName,
      lastName,
      loading,
      loadingSucceeded,
      ownFirmOnly,
      selectedParticipant,
      showResults,
      results,
      limit,
      offset,
      firmOptions,
      options,
      componentAttributes,
      fullAddress,
      openModal,
      closeModal,
      resetData,
      handleSelectAgent,
      handleSubmit,
      handlePageChange,
      v$,
      buttonWarning
    };
  }
});